import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    DocumentIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EyeIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface Building {
    _id: string;
    nombre: string;
    ciudad: string;
}

interface User {
    _id: string;
    nombre: string;
    apellido: string;
    email: string;
}

interface Document {
    _id: string;
    nombre: string;
    descripcion: string;
    etiqueta: string;
    tipo: 'financiero' | 'legal' | 'contable' | 'comunicado' | 'general';
    fechaCaducidad: string | null;
    edificios: Building[];
    usuarios: User[];
    archivo: {
        nombreOriginal: string;
        nombreArchivo: string;
        ruta: string;
        tamaño: number;
        tipoMime: string;
    };
    creadoPor: {
        _id: string;
        nombre: string;
        apellido: string;
        email: string;
    };
    activo: boolean;
    createdAt: string;
    updatedAt: string;
}

interface DocumentFormData {
    nombre: string;
    descripcion: string;
    etiqueta: string;
    tipo: 'financiero' | 'legal' | 'contable' | 'comunicado' | 'general';
    fechaCaducidad: string;
    edificios: string[];
    usuarios: string[];
    archivo: File | null;
}

const DOCUMENTS_PAGE_SIZE = 50;

const DocumentManagement: React.FC = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [formData, setFormData] = useState<DocumentFormData>({
        nombre: '',
        descripcion: '',
        etiqueta: '',
        tipo: 'general',
        fechaCaducidad: '',
        edificios: [],
        usuarios: [],
        archivo: null
    });

    useEffect(() => {
        fetchDocuments();
        fetchBuildings();
        fetchUsers();
    }, []);

    const fetchDocuments = async (pageNum: number = 1, append: boolean = false) => {
        if (pageNum === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            params.append('limit', String(DOCUMENTS_PAGE_SIZE));
            params.append('page', String(pageNum));
            if (searchTerm) params.append('searchTerm', searchTerm);
            if (filterType) params.append('tipo', filterType);
            if (filterStatus === 'activo') params.append('activo', 'true');
            else if (filterStatus === 'inactivo') params.append('activo', 'false');

            const url = apiUrl(`/api/documents?${params}`);
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const newDocs = data.data.documents || [];
                if (append) {
                    setDocuments(prev => [...prev, ...newDocs]);
                } else {
                    setDocuments(newDocs);
                }
                setPage(pageNum);
                setTotalPages(data.data.totalPages ?? 0);
                setTotal(data.data.total ?? 0);
            } else {
                const error = await response.json();
                console.error('Error al obtener documentos:', error);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchBuildings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/buildings'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setBuildings(data.data.buildings);
            } else {
                console.error('Error al obtener edificios');
            }
        } catch (error) {
            console.error('Error al obtener edificios:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/admin/users'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUsers(data.data.users);
                setFilteredUsers(data.data.users); // Inicialmente mostrar todos los usuarios
            } else {
                console.error('Error al obtener usuarios');
            }
        } catch (error) {
            console.error('Error al obtener usuarios:', error);
        }
    };

    const filterUsersByBuildings = async (selectedBuildingIds: string[]) => {
        if (selectedBuildingIds.length === 0) {
            setFilteredUsers(users);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/admin/properties'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const properties = data.data.properties;
                
                // Obtener usuarios que tienen propiedades en los edificios seleccionados
                const userIds = new Set<string>();
                properties.forEach((property: any) => {
                    if (selectedBuildingIds.includes(property.edificio?._id)) {
                        property.propietarios.forEach((propietario: any) => {
                            userIds.add(propietario.usuario._id);
                        });
                    }
                });

                // Filtrar usuarios que están en la lista de IDs
                const filtered = users.filter(user => userIds.has(user._id));
                setFilteredUsers(filtered);
            } else {
                console.error('Error al obtener propiedades para filtrar usuarios');
                setFilteredUsers(users);
            }
        } catch (error) {
            console.error('Error al filtrar usuarios:', error);
            setFilteredUsers(users);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validación del frontend
        if (!editingDocument && !formData.archivo) {
            alert('Debe seleccionar un archivo para crear el documento');
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            const url = editingDocument ? apiUrl(`/api/documents/${editingDocument._id}`) : apiUrl('/api/documents');
            const method = editingDocument ? 'PUT' : 'POST';
            
            console.log('Enviando datos:', formData);
            console.log('URL:', url);
            console.log('Método:', method);
            
            const formDataToSend = new FormData();
            formDataToSend.append('nombre', formData.nombre);
            formDataToSend.append('descripcion', formData.descripcion);
            formDataToSend.append('etiqueta', formData.etiqueta);
            formDataToSend.append('tipo', formData.tipo);
            if (formData.fechaCaducidad) {
                formDataToSend.append('fechaCaducidad', formData.fechaCaducidad);
            }
            
            // Agregar edificios como array
            formData.edificios.forEach(edificioId => {
                formDataToSend.append('edificios[]', edificioId);
            });
            
            // Agregar usuarios como array
            formData.usuarios.forEach(usuarioId => {
                formDataToSend.append('usuarios[]', usuarioId);
            });
            
            if (formData.archivo && !editingDocument) {
                formDataToSend.append('archivo', formData.archivo);
            }
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataToSend
            });

            console.log('Respuesta del servidor:', response.status, response.statusText);

            if (response.ok) {
                const result = await response.json();
                console.log('Documento creado/actualizado:', result);
                await fetchDocuments();
                setShowModal(false);
                setEditingDocument(null);
                setFormData({
                    nombre: '',
                    descripcion: '',
                    etiqueta: '',
                    tipo: 'general',
                    fechaCaducidad: '',
                    edificios: [],
                    usuarios: [],
                    archivo: null
                });
                alert('Documento guardado exitosamente');
            } else {
                const error = await response.json();
                console.error('Error del servidor:', error);
                
                // Mostrar errores específicos de validación
                if (error.errors && error.errors.length > 0) {
                    const errorMessages = error.errors.map((err: any) => err.msg).join('\n');
                    alert(`Errores de validación:\n${errorMessages}`);
                } else {
                    alert(error.message || 'Error al guardar documento');
                }
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar documento');
        }
    };

    const handleEdit = (document: Document) => {
        setEditingDocument(document);
        const edificiosIds = document.edificios.map(e => e._id);
        setFormData({
            nombre: document.nombre,
            descripcion: document.descripcion,
            etiqueta: document.etiqueta || '',
            tipo: document.tipo,
            fechaCaducidad: document.fechaCaducidad ? document.fechaCaducidad.split('T')[0] : '',
            edificios: edificiosIds,
            usuarios: document.usuarios.map(u => u._id),
            archivo: null
        });
        // Filtrar usuarios basado en los edificios del documento
        filterUsersByBuildings(edificiosIds);
        setShowModal(true);
    };

    const handleDelete = async (documentId: string) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/documents/${documentId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await fetchDocuments();
                alert('Documento eliminado exitosamente');
            } else {
                const error = await response.json();
                alert(error.message || 'Error al eliminar documento');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar documento');
        }
    };

    const handleDownload = async (documentId: string, nombreOriginal: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/documents/${documentId}/download`), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = nombreOriginal;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                const error = await response.json();
                alert(error.message || 'Error al descargar documento');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar documento');
        }
    };

    const getTypeColor = (tipo: string) => {
        switch (tipo) {
            case 'financiero': return 'bg-green-100 text-green-800';
            case 'legal': return 'bg-blue-100 text-blue-800';
            case 'contable': return 'bg-purple-100 text-purple-800';
            case 'comunicado': return 'bg-yellow-100 text-yellow-800';
            case 'general': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const hasMorePages = totalPages > 1 && page < totalPages;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Gestión de Documentos</h2>
                    <p className="text-gray-600 mt-2">Administra documentos compartidos con los socios</p>
                </div>
                <button
                    onClick={() => {
                        setEditingDocument(null);
                        setFormData({
                            nombre: '',
                            descripcion: '',
                            etiqueta: '',
                            tipo: 'general',
                            fechaCaducidad: '',
                            edificios: [],
                            usuarios: [],
                            archivo: null
                        });
                        setFilteredUsers(users); // Resetear filtro de usuarios
                        setShowModal(true);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Nuevo Documento</span>
                </button>
            </div>

            {/* Filtros */}
            <div className="bg-white p-4 rounded-lg shadow">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar documentos..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los tipos</option>
                            <option value="financiero">Financiero</option>
                            <option value="legal">Legal</option>
                            <option value="contable">Contable</option>
                            <option value="comunicado">Comunicado</option>
                            <option value="general">General</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los estados</option>
                            <option value="activo">Activo</option>
                            <option value="inactivo">Inactivo</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => fetchDocuments(1, false)}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
                        >
                            <FunnelIcon className="h-4 w-4" />
                            <span>Filtrar</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Lista de documentos */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        Documentos {total > 0 ? `(${documents.length}${total > documents.length ? ` de ${total}` : ''})` : ''}
                    </h3>
                </div>
                <div className="divide-y divide-gray-200">
                    {documents.length === 0 ? (
                        <div className="px-6 py-12 text-center">
                            <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay documentos</h3>
                            <p className="text-gray-500">No se encontraron documentos que coincidan con los filtros.</p>
                        </div>
                    ) : (
                        documents.map((document) => (
                            <div key={document._id} className="px-6 py-4 hover:bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3">
                                            <DocumentIcon className="h-8 w-8 text-blue-500" />
                                            <div>
                                                <div className="flex items-center space-x-2">
                                                    <h4 className="text-lg font-medium text-gray-900">{document.nombre}</h4>
                                                    {document.etiqueta && (
                                                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                                            {document.etiqueta}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600">{document.descripcion}</p>
                                            </div>
                                        </div>

                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                            <div className="flex items-center space-x-2">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(document.tipo)}`}>
                                                    {document.tipo}
                                                </span>
                                            </div>
                                            
                                            <div className="flex items-center space-x-2">
                                                <CalendarIcon className="h-4 w-4" />
                                                <span>
                                                    {document.fechaCaducidad ? 
                                                        new Date(document.fechaCaducidad).toLocaleDateString() : 
                                                        'Sin caducidad'
                                                    }
                                                </span>
                                            </div>
                                            
                                            <div className="flex items-center space-x-2">
                                                <BuildingOfficeIcon className="h-4 w-4" />
                                                <span>
                                                    {document.edificios.length > 0 ? 
                                                        document.edificios.map(e => e.nombre).join(', ') : 
                                                        'Todos los edificios'
                                                    }
                                                </span>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <UserGroupIcon className="h-4 w-4" />
                                                <span>
                                                    {document.usuarios.length > 0 ? 
                                                        `${document.usuarios.length} usuario(s)` : 
                                                        'Todos los usuarios'
                                                    }
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-2 text-xs text-gray-500">
                                            <span>Archivo: {document.archivo.nombreOriginal}</span>
                                            <span className="mx-2">•</span>
                                            <span>Tamaño: {formatFileSize(document.archivo.tamaño)}</span>
                                            <span className="mx-2">•</span>
                                            <span>Creado: {new Date(document.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleDownload(document._id, document.archivo.nombreOriginal)}
                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                                            title="Descargar"
                                        >
                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleEdit(document)}
                                            className="p-2 text-yellow-600 hover:bg-yellow-100 rounded-lg"
                                            title="Editar"
                                        >
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(document._id)}
                                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                                            title="Eliminar"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    {hasMorePages && (
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
                            <button
                                type="button"
                                onClick={() => fetchDocuments(page + 1, true)}
                                disabled={loadingMore}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {loadingMore ? (
                                    <>
                                        <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                        Cargando...
                                    </>
                                ) : (
                                    'Cargar más'
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {editingDocument ? 'Editar Documento' : 'Nuevo Documento'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre del Documento
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción (opcional)
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Etiqueta (opcional)
                                </label>
                                <input
                                    type="text"
                                    value={formData.etiqueta}
                                    onChange={(e) => setFormData({ ...formData, etiqueta: e.target.value })}
                                    placeholder="Ej: IMPORTANTE, URGENTE, REVISAR"
                                    maxLength={50}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Etiqueta corta que aparecerá junto al nombre del archivo
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tipo de Documento
                                    </label>
                                    <select
                                        required
                                        value={formData.tipo}
                                        onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'financiero' | 'legal' | 'contable' | 'comunicado' | 'general' })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="general">General</option>
                                        <option value="financiero">Financiero</option>
                                        <option value="legal">Legal</option>
                                        <option value="contable">Contable</option>
                                        <option value="comunicado">Comunicado</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fecha de Caducidad (opcional)
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.fechaCaducidad}
                                        onChange={(e) => setFormData({ ...formData, fechaCaducidad: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {!editingDocument && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Archivo
                                    </label>
                                    <input
                                        type="file"
                                        required={!editingDocument}
                                        onChange={(e) => setFormData({ ...formData, archivo: e.target.files?.[0] || null })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Formatos permitidos: PDF, Word, Excel, PowerPoint, TXT, JPG, PNG, GIF (máx. 10MB)
                                    </p>
                                </div>
                            )}

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Edificios (opcional - si no se selecciona ninguno, será visible en todos)
                                    </label>
                                    {formData.edificios.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData({ ...formData, edificios: [], usuarios: [] });
                                                setFilteredUsers(users);
                                            }}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        >
                                            Limpiar selección
                                        </button>
                                    )}
                                </div>
                                <select
                                    multiple
                                    value={formData.edificios}
                                    onChange={(e) => {
                                        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                        setFormData({ ...formData, edificios: selectedOptions, usuarios: [] }); // Limpiar usuarios al cambiar edificios
                                        // Filtrar usuarios basado en los edificios seleccionados
                                        filterUsersByBuildings(selectedOptions);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    size={3}
                                >
                                    {buildings.map((building) => (
                                        <option key={building._id} value={building._id}>
                                            {building.nombre} - {building.ciudad}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Usuarios Específicos (opcional - si no se selecciona ninguno, será visible para todos)
                                </label>
                                {formData.edificios.length > 0 && (
                                    <p className="text-xs text-blue-600 mb-2">
                                        💡 Mostrando {filteredUsers.length} usuario(s) que tienen propiedades en los edificios seleccionados
                                    </p>
                                )}
                                <select
                                    multiple
                                    value={formData.usuarios}
                                    onChange={(e) => {
                                        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                        setFormData({ ...formData, usuarios: selectedOptions });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    size={3}
                                >
                                    {filteredUsers.map((user) => (
                                        <option key={user._id} value={user._id}>
                                            {user.nombre} {user.apellido} ({user.email})
                                        </option>
                                    ))}
                                </select>
                                {filteredUsers.length === 0 && formData.edificios.length > 0 && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        No hay usuarios con propiedades en los edificios seleccionados
                                    </p>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    {editingDocument ? 'Actualizar' : 'Crear'} Documento
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentManagement;
